import { useNavigate, useParams } from "@tanstack/react-router";
import { Button, SectionCard } from "@/deps/ui-library";
import { useApproveDebitNote, useDebitNote } from "@/api/queries";
import {
  ApprovalActions,
  ApprovalCard,
  useApprovalHoldsDraft,
} from "@/components/DocumentApproval";
import { useCan } from "@/app/session";
import { DocumentScreen, LinesTable } from "@/components/DocumentScreen";
import { DraftActions } from "@/components/DraftActions";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { useTaxWords } from "@/app/session";

export function DebitNoteDetailPage() {
  const say = useTaxWords();
  const { id } = useParams({ from: "/purchases/debit-notes/$id" });
  const { data, isPending, error } = useDebitNote(id);
  const approve = useApproveDebitNote();
  const held = useApprovalHoldsDraft("debitNote", id, true);
  const can = useCan();
  const navigate = useNavigate();

  if (isPending) return <p className="text-fg-muted p-6 text-sm">Loading debit note…</p>;
  if (error || !data)
    return <p className="text-bad-text p-6 text-sm">That debit note could not be loaded.</p>;

  const against = data.againstNumber ?? "The supplier's account";
  const standing = data.againstNumber
    ? `against ${data.againstNumber}`
    : "on the supplier's account";

  return (
    <DocumentScreen
      title="Debit note"
      number={data.number}
      status={data.status}
      locked={data.status === "approved"}
      subtitle={`${data.supplierName} · ${standing}`}
      actions={
        data.status !== "approved" ? (
          <span className="flex items-center gap-2">
            {approve.isError ? (
              <span role="alert" className="text-bad-text text-xs">
                {approve.error instanceof Error
                  ? approve.error.message
                  : "That could not be approved."}
              </span>
            ) : null}
            {!held && can("purchases.debitnotes.create") ? (
              <DraftActions
                kind="debitNotes"
                label="debit note"
                id={id}
                onEdit={() =>
                  void navigate({ to: "/purchases/debit-notes/$id/edit", params: { id } })
                }
                onDeleted={() => void navigate({ to: "/purchases/debit-notes" })}
              />
            ) : null}
            <ApprovalActions
              kind="debitNote"
              id={id}
              canCreate={can("purchases.debitnotes.create")}
              canIssue={can("purchases.debitnotes.approve")}
              issuing={approve.isPending}
              onIssue={() => approve.mutate(id)}
              issueLabel="Approve and post"
              approveLabel="Approve and post"
              workingLabel="Approving"
            />
          </span>
        ) : undefined
      }
      facts={[
        { label: "Supplier", value: data.supplierName },
        { label: "Date", value: formatDate(data.date) },
        { label: "Against", value: <span className="num">{against}</span> },
        { label: "Reason", value: data.reason ?? "" },
        ...(data.status === "approved" && (!data.againstNumber || Number(data.available) > 0)
          ? [
              {
                label: "Left to use",
                value: <span className="num">{formatAmount(data.available)}</span>,
              },
            ]
          : []),
        ...(data.reasonDetail ? [{ label: "Details", value: data.reasonDetail }] : []),
      ]}
      history={data.history}
      aside={<ApprovalCard kind="debitNote" id={id} />}
    >
      <SectionCard title="Lines" flush>
        <LinesTable
          caption={`Lines of debit note ${data.number ?? "draft"}`}
          lines={data.lines.map((line) => ({
            n: line.n,
            label: line.description,
            account: `${line.accountName}${line.capital ? " · capital" : ""}`,
            quantity: line.quantity,
            unitPrice: formatAmount(line.unitPrice),
            tax: line.taxLabel,
            net: formatAmount(line.lineNet),
          }))}
        />
      </SectionCard>

      <SectionCard title="Amounts">
        <dl className="flex flex-col gap-2 text-sm">
          <div className="flex items-baseline justify-between">
            <dt className="text-fg-muted">Subtotal</dt>
            <dd className="num text-fg">{formatAmount(data.subtotal)}</dd>
          </div>
          <div className="flex items-baseline justify-between">
            <dt className="text-fg-muted">{say("Recoverable VAT reversed")}</dt>
            <dd className="num text-fg">{formatAmount(data.recoverableVat)}</dd>
          </div>
          {Number(data.nonRecoverableTax) > 0 ? (
            <div className="flex items-baseline justify-between">
              <dt className="text-fg-muted">Non-recoverable tax reversed</dt>
              <dd className="num text-fg">{formatAmount(data.nonRecoverableTax)}</dd>
            </div>
          ) : null}
          <div className="border-line-soft flex items-baseline justify-between border-t pt-2 font-semibold">
            <dt className="text-fg">Total debit</dt>
            <dd className="num text-fg">{formatAmount(data.total)}</dd>
          </div>
        </dl>
      </SectionCard>
    </DocumentScreen>
  );
}
